import React, { ComponentProps, MouseEventHandler, forwardRef } from 'react';

import { tailint } from '@pukka/common/utils';
import { twMerge } from 'tailwind-merge';

import { Icon, IconNames } from '../Icon';
import { Loader } from '../Loader';
import { Text, TextProps } from '../Text';

type States = 'normal' | 'disabled' | 'loading';

type Variants = {
  [key: string]: Record<States, string>;
};

type Sizes = {
  [key: string]: {
    container: string;
    content: string;
    variant: TextProps<undefined>['variant'];
    icon: ComponentProps<typeof Icon>['size'];
  };
};

const variants = tailint({
  primary: {
    normal: [
      'text-white', // Text
      'border-brand bg-brand', // Normal
      'hover:border-blue-400 hover:bg-blue-400', // Hover
      'active:border-blue-300 active:bg-blue-300', // Clicking
    ].join(' '),
    disabled: 'border-neutral-300 bg-neutral-300 text-neutral-500',
    loading: 'border-blue-400 bg-blue-400 text-white',
  },
  secondary: {
    normal: [
      'text-brand', // Text
      'border-brand bg-white', // Normal
      'hover:bg-light-blue-50', // Hover
      'active:bg-light-blue-100', // Clicking
    ].join(' '),
    disabled: 'border-neutral-300 bg-neutral-100 text-neutral-500',
    loading: 'border-brand bg-light-blue-50 text-brand',
  },
  danger: {
    normal: [
      'text-white', // Text
      'border-red-500 bg-red-500', // Normal
      'hover:border-red-400 hover:bg-red-400', // Hover
      'active:border-red-300 active:bg-red-300', // Clicking
    ].join(' '),
    disabled: 'border-neutral-300 bg-neutral-300 text-neutral-500',
    loading: 'border-red-400 bg-red-400 text-white',
  },
  success: {
    normal: [
      'text-white', // Text
      'border-green-500 bg-green-500', // Normal
      'hover:border-green-400 hover:bg-green-400', // Hover
      'active:border-green-300 active:bg-green-300', // Clicking
    ].join(' '),
    disabled: 'border-neutral-300 bg-neutral-300 text-neutral-500',
    loading: 'border-green-400 bg-green-400 text-white',
  },
  info: {
    normal: [
      'text-brand', // Text
      'border-blue-200 bg-blue-200', // Normal
      'hover:border-blue-100 hover:bg-blue-100', // Hover
      'active:border-blue-50 active:bg-blue-50', // Clicking
    ].join(' '),
    disabled: 'border-blue-200 bg-blue-200 text-blue-500',
    loading: 'border-blue-200 bg-blue-200 text-blue-500',
  },
  nude: {
    normal: [
      'text-primary', // Text
      'border-transparent bg-transparent', // Normal
      'hover:text-brand', // Hover
      'active:text-blue-400', // Clicking
    ].join(' '),
    disabled: 'border-transparent bg-transparent text-neutral-500',
    loading: 'border-transparent bg-transparent text-brand',
  },
  'nude-blue': {
    normal: [
      'text-brand', // Text
      'border-white bg-white', // Normal
      'hover:border-light-blue-50 hover:bg-light-blue-50', // Hover
      'active:border-light-blue-100 active:bg-light-blue-100', // Clicking
    ].join(' '),
    disabled: 'border-white bg-white text-neutral-500',
    loading: 'border-light-blue-50 bg-light-blue-50 text-brand',
  },
  'nude-red': {
    normal: [
      'text-red-500', // Text
      'border-white bg-white', // Normal
      'hover:border-red-50 hover:bg-red-50', // Hover
      'active:border-red-100 active:bg-red-100', // Clicking
    ].join(' '),
    disabled: 'border-white bg-white text-neutral-500',
    loading: 'border-red-50 bg-red-50 text-red-500',
  },
}) satisfies Variants;

const sizes = {
  none: {
    container: '',
    content: '',
    variant: undefined,
    icon: undefined,
  },
  xl: {
    container: 'min-h-[64px] min-w-[64px] p-4 rounded-xl',
    content: 'gap-2',
    variant: 'title-1',
    icon: 'lg',
  },
  l: {
    container: 'min-h-[56px] min-w-[56px] py-3 px-4 rounded-xl',
    content: 'gap-2',
    variant: 'title-1',
    icon: 'lg',
  },
  m: {
    container: 'min-h-[40px] min-w-[40px] py-1.5 px-2 rounded-md',
    content: 'gap-1',
    variant: 'body-2',
    icon: 'lg',
  },
  s: {
    container: 'min-h-[32px] min-w-[32px] py-1 px-2 rounded-md',
    content: 'gap-1',
    variant: 'body-1',
    icon: 'sm',
  },
} satisfies Sizes;

const roundings = tailint({
  default: 'rounded-xl',
  md: 'rounded-md',
  full: 'rounded-full',
  none: 'rounded-none',
  top: 'rounded-b-none',
  bottom: 'rounded-t-none',
  left: 'rounded-r-none',
  right: 'rounded-l-none',
});

const focuses = tailint({
  default: 'focus-ring',
  none: 'outline-none',
});

type ButtonProps = {
  readonly variant?: keyof typeof variants;
  readonly size?: keyof typeof sizes;
  readonly rounding?: keyof typeof roundings;
  readonly disabled?: boolean;
  readonly loading?: boolean;
  readonly focus?: keyof typeof focuses;
  readonly block?: boolean;
  readonly icon?: IconNames;
  readonly reverse?: boolean;
  readonly onClick?: MouseEventHandler<HTMLButtonElement>;
} & React.ButtonHTMLAttributes<HTMLButtonElement>;

const BaseButton = forwardRef<HTMLButtonElement, ButtonProps>(function Button(props, ref) {
  const {
    children,
    className,
    variant = 'primary',
    type = 'button',
    size = 'm',
    rounding,
    focus = 'default',
    disabled = false,
    loading = false,
    block = false,
    icon,
    reverse,
    ...rest
  } = props;
  const textClass = twMerge(
    loading && 'opacity-0',
    'flex flex-row items-center justify-center',
    sizes[size].content,
    icon && children && 'justify-between',
    reverse && 'flex-row-reverse',
  );
  let state: States = 'normal';

  if (disabled) {
    state = 'disabled';
  } else if (loading) {
    state = 'loading';
  }

  return (
    <button
      ref={ref}
      aria-disabled={disabled || loading}
      disabled={disabled || loading}
      className={twMerge(
        'relative h-fit font-medium leading-normal',
        'duration-300 ease-in-out',
        'text-ellipsis whitespace-nowrap',
        'border',
        block && 'w-full',
        focuses[focus],
        variants[variant][state],
        sizes[size].container,
        rounding && roundings[rounding],
        className,
      )}
      // eslint-disable-next-line react/button-has-type
      type={type}
      {...rest}
    >
      <Text as="span" variant={sizes[size].variant} className={textClass}>
        {icon && <Icon icon={icon} size={sizes[size].icon} />}
        {children}
      </Text>
      {loading && (
        <div className="absolute inset-0 flex items-center justify-center">
          <Loader type="ellipsis" />
        </div>
      )}
    </button>
  );
});

export { BaseButton };
